import React, { type FC, useEffect, useState, useRef, useCallback } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/ModalStyle.css';

import { CreateEventForm } from './CreateEventForm';
import { useProcessingOverlay } from '../../context/ProcessingOverlayContext';
import { ReactComponent as ChangeBoatIcon } from '../../react-icons/double-arrow.svg';
import { ReactComponent as CrossIcon } from '../../react-icons/plusIcon.svg';
import { ReactComponent as UploadIcon } from '../../react-icons/uploadIcon.svg';
import { showMessage } from '../../showMessage';
import { getApiErrorMessage } from '../../utils/apiError';
import { pollUploadJob } from '../../utils/uploadJobPolling';

import type { SharingMetadata } from '../../types';

interface ModalProperties {
  isVisible: boolean;
  onClose: () => void;
  userId: string;
  onUploadSuccess: (eventIdSubido: number) => void;
}

interface EventsResponse {
  success: boolean;
  events: Event[];
}

interface Event {
  id: number;
  event_name: string;
  event_date: string | null;
  created_at: string;
  sharing?: SharingMetadata;
}

interface Boat {
  id: number;
  boat_name: string;
  sharing?: SharingMetadata;
}

interface GetUserBoatsResponse {
  success: boolean;
  userBoats: Array<{ id: number; name: string; sharing?: SharingMetadata }>;
}

interface UploadStartResponse {
  success: boolean;
  processing?: boolean;
  jobId?: string;
  message?: string;
}

// Legacy constants removed in favor of i18n keys

const reloadPageIfOnReports = (): void => {
  if (window.location.href.includes('/reports')) {
    window.dispatchEvent(new CustomEvent('reports:refresh'));
  }
};

const NOTIF_ERROR = 'notifications.error';
const ERR_MISSING_API = 'errors.missingApiBase';
const ERR_MISSING_TOKEN = 'errors.missingToken';
const MODALS_NO_FILE = 'modals.noFileSelected';
const MODALS_NO_EVENT = 'modals.noEventSelected';
const MODALS_UNEXPECTED_ERROR = 'modals.unexpectedError';
const MODALS_UPLOAD_AND_GENERATE = 'modals.uploadAndGenerate';
const MODALS_UPLOAD_FILE = 'modals.uploadFile';
const MODALS_UPLOAD_AND_GENERATE_SUCCESS = 'modals.uploadAndGenerateSuccess';
const MODALS_UPLOAD_SUCCESS = 'modals.uploadSuccess';
const MODALS_SELECT_HINT = 'modals.selectFilesHint';
const ACCEPTED_FILE_TYPES = '.csv,.gpx';
const ACCEPTED_UPLOAD_EXTENSIONS = new Set(['.csv', '.gpx']);

const isAcceptedUploadFile = (candidate: File) => {
  const extensionStart = candidate.name.lastIndexOf('.');
  if (extensionStart < 0) {
    return false;
  }
  return ACCEPTED_UPLOAD_EXTENSIONS.has(candidate.name.slice(extensionStart).toLowerCase());
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const ModalReports: FC<ModalProperties> = ({ isVisible, onClose, userId, onUploadSuccess }) => {
  const { t } = useTranslation('common');
  const { startProcess, updateProcess, endProcess } = useProcessingOverlay();
  const fileInputReference = useRef<HTMLInputElement>(null);
  const [boats, setBoats] = useState<Boat[]>([]);
  const [selectedBoatId, setSelectedBoatId] = useState<number | null>(null);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateEventForm, setShowCreateEventForm] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Function to fetch boats associated with the user
  const fetchBoats = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('token');
    if (!baseUrl) {
      setError(t(ERR_MISSING_API));
      setIsLoading(false);
      return;
    }
    if (!token) {
      setError(t(ERR_MISSING_TOKEN));
      setIsLoading(false);
      return;
    }
    const getBoatsUrl = `${baseUrl}/api/user/getUserBoats`;
    axios
      .post<GetUserBoatsResponse>(
        getBoatsUrl,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.userBoats)) {
          // Map response to adjust to expected format
          const boatsData: Boat[] = response.data.userBoats
            .filter((boat) => (boat.sharing ? boat.sharing.isOwner : true))
            .map((boat) => ({
              id: boat.id,
              boat_name: boat.name,
            }));
          setBoats(boatsData);
          setSelectedBoatId((previousSelected) =>
            previousSelected !== null && boatsData.some((boat) => boat.id === previousSelected)
              ? previousSelected
              : null,
          );
        } else {
          setError(t(MODALS_UNEXPECTED_ERROR));
        }
        setIsLoading(false);
        return null;
      })
      .catch(() => {
        setError(t('modals.errorFetchingBoats'));
        setIsLoading(false);
      });
  }, []);

  // Function to fetch events of the selected boat
  const fetchEvents = useCallback(() => {
    if (!selectedBoatId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('token');
    if (!baseUrl) {
      setError(t(ERR_MISSING_API));
      setIsLoading(false);
      return;
    }
    if (!token) {
      setError(t(ERR_MISSING_TOKEN));
      setIsLoading(false);
      return;
    }
    const getEventsUrl = `${baseUrl}/api/user/getEvents`;
    axios
      .post<EventsResponse>(
        getEventsUrl,
        { token, boat_id: selectedBoatId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.events)) {
          const ownEvents = response.data.events.filter((event) =>
            event.sharing ? event.sharing.isOwner : true,
          );
          const sortedEvents = sortEvents(ownEvents);
          setUserEvents(sortedEvents);
          setSelectedEventId((previousSelected) => {
            if (
              previousSelected !== null &&
              sortedEvents.some((event) => event.id === previousSelected)
            ) {
              return previousSelected;
            }
            const firstEvent = sortedEvents[0];
            return firstEvent ? firstEvent.id : null;
          });
          setHoveredEventId((previousHovered) => {
            if (
              previousHovered !== null &&
              sortedEvents.some((event) => event.id === previousHovered)
            ) {
              return previousHovered;
            }
            const firstEvent = sortedEvents[0];
            return firstEvent ? firstEvent.id : null;
          });
        } else {
          setError(t(MODALS_UNEXPECTED_ERROR));
        }
        setIsLoading(false);
        return null;
      })
      .catch(() => {
        setError(t('modals.errorFetchingEvents'));
        setIsLoading(false);
      });
  }, [selectedBoatId]);

  useEffect(() => {
    if (isVisible && boats.length === 0) {
      fetchBoats();
    }
  }, [isVisible, boats.length, fetchBoats]);

  useEffect(() => {
    if (isVisible && selectedBoatId !== null) {
      fetchEvents();
    }
  }, [isVisible, selectedBoatId, fetchEvents]);

  const sortEvents = (events: Event[]): Event[] => {
    return [...events].sort((a, b) => {
      const timeA = a.event_date ? new Date(a.event_date).getTime() : null;
      const timeB = b.event_date ? new Date(b.event_date).getTime() : null;
      if (timeA !== null && timeB !== null) {
        return timeB - timeA;
      }
      if (timeA !== null) {
        return -1;
      }
      if (timeB !== null) {
        return 1;
      }
      return a.event_name.localeCompare(b.event_name);
    });
  };

  const handleUploadAreaClick = () => {
    if (isUploading) {
      return;
    }
    if (fileInputReference.current) {
      fileInputReference.current.click();
    }
  };

  const handleCreateNewEvent = () => {
    setShowCreateEventForm(true);
  };

  const handleEventCreated = () => {
    setShowCreateEventForm(false);
    fetchEvents();
  };

  const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile) {
      if (!isAcceptedUploadFile(selectedFile)) {
        showMessage(
          'error',
          t(NOTIF_ERROR),
          t('modals.unsupportedFileType', 'Only CSV and GPX files are supported.'),
        );
        event.target.value = '';
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!isAcceptedUploadFile(droppedFile)) {
        showMessage(
          'error',
          t(NOTIF_ERROR),
          t('modals.unsupportedFileType', 'Only CSV and GPX files are supported.'),
        );
        return;
      }
      setFile(droppedFile);
      setFileName(droppedFile.name);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const uploadFile = (generateReport: boolean) => {
    if (isUploading) {
      return;
    }
    if (!file) {
      showMessage('error', t(NOTIF_ERROR), t(MODALS_NO_FILE));
      return;
    }

    if (!selectedEventId) {
      showMessage('error', t(NOTIF_ERROR), t(MODALS_NO_EVENT));
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('token');
    if (!baseUrl) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_API));
      return;
    }
    if (!token) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_TOKEN));
      return;
    }

    const eventIdAtStart = selectedEventId;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('event_id', eventIdAtStart.toString());

    const url = generateReport
      ? `${baseUrl}/api/user/uploadCSVAndGenerateReport`
      : `${baseUrl}/api/user/uploadCSV`;

    const eventName = userEvents.find((item) => item.id === eventIdAtStart)?.event_name || t('common.labels.event');
    const processId = startProcess(generateReport ? 'generate' : 'upload', {
      label: generateReport ? t(MODALS_UPLOAD_AND_GENERATE) : t(MODALS_UPLOAD_FILE),
      sublabel: `${file.name} · ${eventName}`,
      progress: 5,
    });

    setIsUploading(true);
    axios
      .post<UploadStartResponse>(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(async (response) => {
        const jobId = response.data?.jobId;
        if (!jobId) {
          throw new Error('The server did not return an upload job id');
        }
        const completedJob = await pollUploadJob({
          baseUrl,
          token,
          jobId,
          onUpdate: (job) => {
            updateProcess(processId, {
              label: job.message || (generateReport ? t(MODALS_UPLOAD_AND_GENERATE) : t(MODALS_UPLOAD_FILE)),
              sublabel: `${file.name} · ${eventName}`,
              progress: job.progress,
            });
          },
        });
        if (generateReport) {
          reloadPageIfOnReports();
          showMessage('success', t('notifications.success'), t(MODALS_UPLOAD_AND_GENERATE_SUCCESS));
        } else {
          showMessage('success', t('notifications.success'), t(MODALS_UPLOAD_SUCCESS));
        }
        if (completedJob.warnings?.length) {
          showMessage('info', t('notifications.info'), completedJob.warnings.join(' '));
        }
        if (eventIdAtStart) {
          onUploadSuccess(eventIdAtStart);
        }
        endProcess(processId);
        setIsUploading(false);
        return null;
      })
      .catch((error) => {
        console.error('Error uploading the file:', error);
        const status = error?.response?.status;
        const fallbackMessage =
          status === 401
            ? t('auth.sessionExpired', 'Your session has expired. Please log in again.')
            : getApiErrorMessage(error, t(MODALS_UNEXPECTED_ERROR));
        showMessage('error', t(NOTIF_ERROR), fallbackMessage);
        endProcess(processId);
        setIsUploading(false);
        return null;
      });
  };

  const handleEventSelection = (eventId: number) => {
    if (eventId === 0) {
      handleCreateNewEvent();
    } else {
      setSelectedEventId(eventId);
    }
  };

  const handleEventMouseEnter = (eventId: number) => {
    setHoveredEventId(eventId);
  };

  const handleEventMouseLeave = () => {
    setHoveredEventId(null);
  };

  // Function to return to boat selection
  const handleChangeBoat = () => {
    setSelectedBoatId(null);
    setUserEvents([]);
    setSelectedEventId(null);
  };

  const modalContainerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    document.body.classList.add('sailtrack-modal-open');
    const handleMouseDown = (event: MouseEvent) => {
      const container = modalContainerReference.current;
      if (container && !container.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.classList.remove('sailtrack-modal-open');
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div
        className={`modal-content-reports ${showCreateEventForm ? 'transparent' : ''}`}
        role="dialog"
        aria-modal="true"
        ref={modalContainerReference}
      >
        {showCreateEventForm ? (
          <CreateEventForm
            onClose={() => setShowCreateEventForm(false)}
            onEventCreated={handleEventCreated}
          />
        ) : selectedBoatId === null ? (
          <div className="boat-selection" data-label={t('common.labels.boat').toUpperCase()}>
            <h2>{t('modals.selectBoat')}</h2>
            {isLoading ? (
              <p>{t('modals.loadingBoats')}</p>
            ) : error ? (
              <p>{t(NOTIF_ERROR)}: {error}</p>
            ) : boats.length === 0 ? (
              <p>{t('modals.noBoats')}</p>
            ) : (
              <div className="boats-list">
                {boats.map((boat) => {
                  const isBoatSelected = selectedBoatId === boat.id;
                  return (
                    <button
                      key={boat.id}
                      type="button"
                      onClick={() => setSelectedBoatId(boat.id)}
                      className={`boat-option${isBoatSelected ? ' selected' : ''}`}
                      aria-pressed={isBoatSelected}
                    >
                      {boat.boat_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="change-boat-container">
              <button
                type="button"
                onClick={handleChangeBoat}
                className="change-boat-button"
              >
                <ChangeBoatIcon />
                {t('modals.changeBoat')}
              </button>
            </div>
            <div className="flex-container">
              <div className="left-container">
                <div className="event-area" data-label={t('common.labels.event').toUpperCase()}>
                  <div className="create-event-container" />
                  <div className="events-list">
                    <button
                      type="button"
                      className={`create-new-event ${0 === selectedEventId ? 'selected' : ''} ${
                        0 === hoveredEventId ? 'hovered' : ''
                      }`}
                      onClick={() => handleEventSelection(0)}
                      onMouseEnter={() => handleEventMouseEnter(0)}
                      onMouseLeave={handleEventMouseLeave}
                      aria-pressed={0 === selectedEventId}
                    >
                      <CrossIcon className="plus-icon" />
                      {t('modals.createNewEvent')}
                    </button>
                    {isLoading ? (
                      <p>{t('modals.loadingEvents')}</p>
                    ) : error ? (
                      <p>{t(NOTIF_ERROR)}: {error}</p>
                    ) : userEvents.length === 0 ? null : (
                      <>
                        <div className="white-bar" />
                        {userEvents.map((event) => (
                          <button
                            type="button"
                            key={event.id}
                            className={`${event.id === selectedEventId ? 'selected' : ''} ${
                              event.id === hoveredEventId ? 'hovered' : ''
                            }`}
                            onClick={() => handleEventSelection(event.id)}
                            onMouseEnter={() => handleEventMouseEnter(event.id)}
                            onMouseLeave={handleEventMouseLeave}
                            aria-pressed={event.id === selectedEventId}
                          >
                            {event.event_name}
                            {event.event_date &&
                              ` - ${new Date(event.event_date).toLocaleDateString()}`}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <div className="button-group">
                  <button
                    className={`green-button ${file && !isUploading ? '' : 'disabled'}`}
                    onClick={() => uploadFile(true)}
                    disabled={isUploading || !file}
                  >
                    {t('modals.uploadAndGenerate')}
                  </button>
                  <button
                    className={`button-upload ${file && !isUploading ? '' : 'disabled'}`}
                    onClick={() => uploadFile(false)}
                    disabled={isUploading || !file}
                  >
                    {t('modals.uploadFile')}
                  </button>
                </div>
              </div>
              <div className="upload-container">
                <div
                  className={`upload-area ${isDragOver ? 'drag-over' : ''} ${
                    file ? 'uploaded' : ''
                  }`}
                  onClick={handleUploadAreaClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  aria-busy={isUploading}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleUploadAreaClick();
                    }
                  }}
                >
                  <UploadIcon className="upload-icon" />
                  {file ? <p>{fileName}</p> : <p>{t(MODALS_SELECT_HINT)}</p>}
                  <input
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    ref={fileInputReference}
                    style={{ display: 'none' }}
                    onChange={handleFileUploadChange}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ModalReports;
